class LightboxZoom {
  constructor() {
    // Core properties
    this.modal = null;
    this.lightboxImage = null;
    this.currentIndex = 0;
    this.images = [];
    
    // Zoom properties
    this.zoomLevel = 1;
    this.maxZoom = 3;
    this.minZoom = 1;
    this.zoomStep = 0.5;
    
    // Drag properties
    this.isDragging = false;
    this.translateX = 0;
    this.translateY = 0;
    
    // Touch properties
    this.touchStartTime = 0;
    this.hasMoved = false;
    
    // Cache DOM elements and initialize
    this.init();
  }

  init() {
    if (!this.cacheElements()) return;
    this.collectImages();
    this.bindEvents();
  }

  cacheElements() {
    this.modal = document.getElementById('lightbox-modal');
    if (!this.modal) return false;
    
    this.lightboxImage = this.modal.querySelector('.lightbox-image');
    this.currentCounter = this.modal.querySelector('.lightbox-current');
    this.totalCounter = this.modal.querySelector('.lightbox-total');
    
    return this.lightboxImage !== null;
  }

  collectImages() {
    // Use more efficient selector and filter
    const triggers = document.querySelectorAll('.lightbox-trigger:not(.main-slider-360-video)');
    
    this.images = Array.from(triggers, (img, index) => ({
      src: img.dataset.lightboxSrc || img.src,
      alt: img.dataset.lightboxAlt || img.alt,
      index
    }));
    
    // Update counter once
    this.updateTotalCounter();
  }

  updateTotalCounter() {
    if (this.totalCounter) {
      this.totalCounter.textContent = this.images.length;
    }
  }

  bindEvents() {
    // Optimized click handler with early returns
    document.addEventListener('click', this.handleDocumentClick.bind(this));
    
    if (!this.modal) return;
    
    // Cache modal elements for better performance
    const closeBtn = this.modal.querySelector('.lightbox-close');
    const prevBtn = this.modal.querySelector('.lightbox-prev');
    const nextBtn = this.modal.querySelector('.lightbox-next');
    const zoomInBtn = this.modal.querySelector('.lightbox-zoom-in');
    const zoomOutBtn = this.modal.querySelector('.lightbox-zoom-out');
    const resetBtn = this.modal.querySelector('.lightbox-reset');
    const overlay = this.modal.querySelector('.lightbox-overlay');
    
    // Bind modal controls
    closeBtn?.addEventListener('click', () => this.closeLightbox());
    prevBtn?.addEventListener('click', () => this.previousImage());
    nextBtn?.addEventListener('click', () => this.nextImage());
    zoomInBtn?.addEventListener('click', () => this.zoomIn());
    zoomOutBtn?.addEventListener('click', () => this.zoomOut());
    resetBtn?.addEventListener('click', () => this.resetZoom());
    
    // Overlay click to close
    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) this.closeLightbox();
    });
    
    // Keyboard and image events
    document.addEventListener('keydown', this.handleKeydown.bind(this));
    
    if (this.lightboxImage) {
      this.lightboxImage.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
      this.lightboxImage.addEventListener('click', this.handleImageClick.bind(this));
      this.bindDragEvents();
    }
  }

  handleDocumentClick(e) {
    // Check if clicked on lightbox trigger image
    if (e.target.classList.contains('lightbox-trigger')) {
      e.preventDefault();
      const index = parseInt(e.target.dataset.lightboxIndex) || 0;
      this.openLightbox(index);
      return;
    }
    
    // Handle 360 spin interactions
    if (e.target.closest('.spin-360-trigger')) return; // Let 360 spin work
    
    if (e.target.classList.contains('spin-360-overlay') || 
        e.target.closest('.spin-360-overlay') ||
        e.target.matches('[data-360-iframe]') || 
        e.target.closest('#wr360holder')) {
      e.preventDefault();
      e.stopPropagation();
      this.openLightbox(0);
    }
  }

  handleKeydown(e) {
    if (!this.modal.classList.contains('active')) return;
    
    const keyActions = {
      'Escape': () => this.closeLightbox(),
      'ArrowLeft': () => this.previousImage(),
      'ArrowRight': () => this.nextImage(),
      '+': () => this.zoomIn(),
      '=': () => this.zoomIn(),
      '-': () => this.zoomOut(),
      '0': () => this.resetZoom()
    };
    
    const action = keyActions[e.key];
    if (action) {
      e.preventDefault();
      action();
    }
  }

  handleWheel(e) {
    e.preventDefault();
    e.deltaY < 0 ? this.zoomIn() : this.zoomOut();
  }

  handleImageClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (this.zoomLevel === this.minZoom) {
      this.zoomLevel = 2;
      this.updateImageTransform();
      this.updateZoomState();
    } else {
      this.resetZoom();
    }
  }

  bindDragEvents() {
    let startX, startY, initialTranslateX, initialTranslateY;

    // Mouse drag events
    this.lightboxImage.addEventListener('mousedown', (e) => {
      if (this.zoomLevel <= 1) return;
      
      this.startDrag(e.clientX, e.clientY);
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      this.handleDrag(e.clientX, e.clientY, startX, startY, initialTranslateX, initialTranslateY);
    });

    document.addEventListener('mouseup', () => this.endDrag());

    // Touch events for mobile
    this.lightboxImage.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      
      this.touchStartTime = Date.now();
      this.hasMoved = false;
      
      if (this.zoomLevel > 1) {
        this.startDrag(e.touches[0].clientX, e.touches[0].clientY);
      }
    });

    this.lightboxImage.addEventListener('touchmove', (e) => {
      if (e.touches.length !== 1) return;
      
      this.hasMoved = true;
      this.handleDrag(e.touches[0].clientX, e.touches[0].clientY, startX, startY, initialTranslateX, initialTranslateY);
      if (this.isDragging) e.preventDefault();
    });

    this.lightboxImage.addEventListener('touchend', (e) => {
      const touchDuration = Date.now() - this.touchStartTime;
      
      // Quick tap without movement = zoom toggle
      if (touchDuration < 300 && !this.hasMoved && e.touches.length === 0) {
        this.handleImageClick(e);
      }
      
      this.endDrag();
    });

    // Store references for drag calculations
    this.lightboxImage.addEventListener('mousedown', (e) => {
      if (this.zoomLevel > 1) {
        startX = e.clientX;
        startY = e.clientY;
        initialTranslateX = this.translateX;
        initialTranslateY = this.translateY;
      }
    });

    this.lightboxImage.addEventListener('touchstart', (e) => {
      if (e.touches.length === 1 && this.zoomLevel > 1) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        initialTranslateX = this.translateX;
        initialTranslateY = this.translateY;
      }
    });
  }

  startDrag(clientX, clientY) {
    this.isDragging = true;
    this.lightboxImage.classList.add('dragging');
  }

  handleDrag(clientX, clientY, startX, startY, initialTranslateX, initialTranslateY) {
    if (!this.isDragging || this.zoomLevel <= 1) return;
    
    this.translateX = initialTranslateX + (clientX - startX);
    this.translateY = initialTranslateY + (clientY - startY);
    this.updateImageTransform();
  }

  endDrag() {
    if (this.isDragging) {
      this.isDragging = false;
      this.lightboxImage.classList.remove('dragging');
    }
    
    // Pinch to zoom for mobile
    this.bindPinchZoom();
  }

  bindPinchZoom() {
    let initialDistance = 0;
    let initialZoom = 1;

    const handlePinchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        initialDistance = this.getDistance(e.touches[0], e.touches[1]);
        initialZoom = this.zoomLevel;
      }
    };

    const handlePinchMove = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
        const scale = currentDistance / initialDistance;
        const newZoom = this.clamp(initialZoom * scale, this.minZoom, this.maxZoom);
        
        if (newZoom !== this.zoomLevel) {
          this.zoomLevel = newZoom;
          this.updateImageTransform();
          this.updateZoomState();
        }
      }
    };

    this.lightboxImage.addEventListener('touchstart', handlePinchStart);
    this.lightboxImage.addEventListener('touchmove', handlePinchMove);
  }

  getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  openLightbox(index = 0) {
    if (!this.images.length || !this.modal) return;
    
    this.currentIndex = this.clamp(index, 0, this.images.length - 1);
    this.loadImage();
    this.resetZoom();
    this.showModal();
    this.updateNavigation();
  }

  closeLightbox() {
    if (!this.modal) return;
    
    this.modal.classList.remove('active');
    setTimeout(() => {
      this.modal.style.display = 'none';
      document.body.style.overflow = '';
    }, 300);
  }

  showModal() {
    this.modal.style.display = 'flex';
    this.modal.offsetHeight; // Force reflow
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  loadImage() {
    const imageData = this.images[this.currentIndex];
    if (!imageData) return;
    
    // Preload image for smooth display
    const img = new Image();
    img.onload = () => {
      this.lightboxImage.src = img.src;
      this.lightboxImage.alt = imageData.alt;
    };
    img.src = imageData.src;
    
    // Update counter
    this.currentCounter && (this.currentCounter.textContent = this.currentIndex + 1);
  }

  previousImage() {
    if (this.images.length <= 1) return;
    this.navigateToImage(this.currentIndex > 0 ? this.currentIndex - 1 : this.images.length - 1);
  }

  nextImage() {
    if (this.images.length <= 1) return;
    this.navigateToImage(this.currentIndex < this.images.length - 1 ? this.currentIndex + 1 : 0);
  }

  navigateToImage(index) {
    this.currentIndex = index;
    this.loadImage();
    this.resetZoom();
    this.updateNavigation();
  }

  updateNavigation() {
    const prevBtn = this.modal.querySelector('.lightbox-prev');
    const nextBtn = this.modal.querySelector('.lightbox-next');
    const shouldShow = this.images.length > 1;
    
    if (prevBtn) prevBtn.style.display = shouldShow ? 'flex' : 'none';
    if (nextBtn) nextBtn.style.display = shouldShow ? 'flex' : 'none';
  }

  zoomIn() {
    if (this.zoomLevel >= this.maxZoom) return;
    this.setZoom(Math.min(this.zoomLevel + this.zoomStep, this.maxZoom));
  }

  zoomOut() {
    if (this.zoomLevel <= this.minZoom) return;
    const newZoom = Math.max(this.zoomLevel - this.zoomStep, this.minZoom);
    
    // Reset translation if zooming out completely
    if (newZoom === this.minZoom) {
      this.translateX = this.translateY = 0;
    }
    
    this.setZoom(newZoom);
  }

  resetZoom() {
    this.zoomLevel = this.minZoom;
    this.translateX = this.translateY = 0;
    this.updateImageTransform();
    this.updateZoomState();
  }

  setZoom(level) {
    this.zoomLevel = level;
    this.updateImageTransform();
    this.updateZoomState();
  }

  updateImageTransform() {
    if (!this.lightboxImage) return;
    
    const transform = `scale(${this.zoomLevel}) translate(${this.translateX / this.zoomLevel}px, ${this.translateY / this.zoomLevel}px)`;
    this.lightboxImage.style.transform = transform;
    this.lightboxImage.style.transformOrigin = 'center center';
  }

  updateZoomState() {
    if (!this.lightboxImage) return;
    
    // Update image class
    this.lightboxImage.classList.toggle('zoomed', this.zoomLevel > 1);
    
    // Update button states
    const zoomInBtn = this.modal.querySelector('.lightbox-zoom-in');
    const zoomOutBtn = this.modal.querySelector('.lightbox-zoom-out');
    
    if (zoomInBtn) zoomInBtn.disabled = this.zoomLevel >= this.maxZoom;
    if (zoomOutBtn) zoomOutBtn.disabled = this.zoomLevel <= this.minZoom;
  }
}

// Initialize when DOM is ready
function initLightbox() {
  new LightboxZoom();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLightbox);
} else {
  initLightbox();
}
